/**
 * A single site's row in Admin Setup > Regional Settings (CR-6256).
 *
 * A site is a binary choice between two buttons: inherit the group company
 * defaults, or override them. While it inherits, the row shows nothing but
 * those two buttons — the values in force are stated once at the top of the
 * page, so repeating three dropdowns per site was duplication. Choosing
 * Override reveals currency, timezone and date format for that site alone.
 *
 * Switching to Override does not change what the site resolves to: the server
 * materialises the values it was already inheriting into its own columns, so
 * the dropdowns open on what is in force and only a deliberate edit moves it.
 */
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { MapPin, Save, Loader2, Calendar, Building2, SlidersHorizontal } from 'lucide-react';
import { SiteRegionalSettings, PreferencesChoices } from './types';
import { formatDatePreview } from './formatDatePreview';

interface SiteRegionalRowProps {
  site: SiteRegionalSettings;
  choices: PreferencesChoices | null;
  edits: Partial<SiteRegionalSettings> | undefined;
  saving: boolean;
  togglingInheritance: boolean;
  onSettingChange: (siteId: number, field: string, value: string | boolean | null) => void;
  onSave: (siteId: number) => void;
  onInheritanceChange: (siteId: number, usesGroupDefaults: boolean) => void;
  /**
   * W2-T1: the group's "judge on the group clock" switch and the timezone it
   * means. Unknown (undefined) where the caller has no group-defaults read —
   * the company-profile tab — and the note is then left out rather than guessed.
   */
  groupClockOn?: boolean;
  groupTimezoneDisplay?: string;
}

export function SiteRegionalRow({
  site,
  choices,
  edits,
  saving,
  togglingInheritance,
  onSettingChange,
  onSave,
  onInheritanceChange,
  groupClockOn,
  groupTimezoneDisplay,
}: SiteRegionalRowProps) {
  const hasEdits = !!edits && Object.keys(edits).length > 0;
  const currentValues = { ...site, ...edits };
  const inherits = currentValues.uses_group_regional_defaults;

  const valueFor = (
    field: 'default_currency' | 'default_timezone' | 'default_date_format',
  ): string => (currentValues[field] as string | null) ?? '';

  /**
   * Re-selecting the mode a site is already in is a no-op — it would otherwise
   * PATCH the flag to the value it already holds and toast as if something had
   * changed. Pending edits are discarded by the hook when the mode does change,
   * since they describe a state the admin has just left.
   */
  const selectMode = (usesGroupDefaults: boolean) => {
    if (usesGroupDefaults === inherits) return;
    onInheritanceChange(site.id, usesGroupDefaults);
  };

  const dateFormatValue = valueFor('default_date_format');

  return (
    <div data-testid={`site-row-${site.id}`} className="border rounded-lg p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          <h4 className="font-semibold">{site.site_name}</h4>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {hasEdits && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              Unsaved Changes
            </Badge>
          )}

          {/* Nothing to save while a site follows the defaults. */}
          {!inherits && (
            <Button
              size="sm"
              onClick={() => onSave(site.id)}
              disabled={!hasEdits || saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Save
            </Button>
          )}
        </div>
      </div>

      {/* The two actions the row offers. The pressed one is the current mode. */}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={inherits ? 'default' : 'outline'}
          aria-pressed={inherits}
          disabled={togglingInheritance}
          onClick={() => selectMode(true)}
        >
          {togglingInheritance && !inherits ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Building2 className="h-4 w-4 mr-1" />
          )}
          Inherit from Group Company
        </Button>

        <Button
          size="sm"
          variant={inherits ? 'outline' : 'default'}
          aria-pressed={!inherits}
          disabled={togglingInheritance}
          onClick={() => selectMode(false)}
        >
          {togglingInheritance && inherits ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <SlidersHorizontal className="h-4 w-4 mr-1" />
          )}
          Override Default Settings
        </Button>
      </div>

      {/* CR-6256: the regional fields exist only for an overriding site. */}
      {!inherits && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Currency */}
          <div className="space-y-2">
            <Label htmlFor={`currency-${site.id}`}>Currency</Label>
            <select
              id={`currency-${site.id}`}
              value={valueFor('default_currency')}
              onChange={(e) => onSettingChange(site.id, 'default_currency', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            >
              {choices?.currencies.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor={`timezone-${site.id}`}>Timezone</Label>
            <select
              id={`timezone-${site.id}`}
              value={valueFor('default_timezone')}
              onChange={(e) => onSettingChange(site.id, 'default_timezone', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            >
              {choices?.timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            {/* W2-T1: an overriding site's timezone always drives what it displays;
                whether it also judges its delivery metrics depends on the group
                clock switch above — say so here, where the timezone is chosen. */}
            {groupClockOn !== undefined && (
              <p className="text-xs text-muted-foreground" data-testid={`timezone-clock-note-${site.id}`}>
                {groupClockOn
                  ? `Display only. "Today" for arrears and period cut-offs comes from the group clock (${groupTimezoneDisplay || 'group timezone'}).`
                  : 'Also sets this site\'s "today" for arrears and period cut-offs.'}
              </p>
            )}
          </div>

          {/* Date Format with live preview */}
          <div className="space-y-2">
            <Label htmlFor={`dateFormat-${site.id}`}>Date Format</Label>
            <select
              id={`dateFormat-${site.id}`}
              value={dateFormatValue}
              onChange={(e) => onSettingChange(site.id, 'default_date_format', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            >
              {choices?.date_formats.map((df) => (
                <option key={df.value} value={df.value}>{df.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>Preview:</span>
              <Badge variant="secondary" className="font-mono">
                {formatDatePreview(dateFormatValue)}
              </Badge>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
